/**
 * Global Contact 상세 → MY Register jsonData 공통 매핑 (프로필·주소 포함)
 */
import { hydrateMyContactDetailForms } from '@/features/Contact/utils/hydrateMyContactDetailForms';

function str(v: unknown): string {
    if (v == null) return '';
    return String(v).trim();
}

function dateOnly(v: unknown): string {
    const s = str(v);
    if (!s) return '';
    return s.split('T')[0].split(' ')[0];
}

function normalizeProfileIsMeeting(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (value === true || value === 1 || value === '1') return 1;
    if (value === false || value === 0 || value === '0') return 0;
    const s = String(value).trim().toLowerCase();
    if (['true', 'yes', 'attendee'].includes(s)) return 1;
    if (['false', 'no', 'non attendee', 'non-attendee'].includes(s)) return 0;
    const n = Number(value);
    if (Number.isNaN(n)) return null;
    return n === 1 ? 1 : 0;
}

export function parseExtendedProfileJsonFields(raw: unknown): {
    issuingCountry: string;
    placeOfBirth: string;
    isMeeting: number | null;
    occupation: string;
    otherDirectorship: string;
    newRegNo: string;
} {
    const empty = {
        issuingCountry: '',
        placeOfBirth: '',
        isMeeting: null as number | null,
        occupation: '',
        otherDirectorship: '',
        newRegNo: '',
    };
    if (raw == null || raw === '') return empty;
    try {
        const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!obj || typeof obj !== 'object') return empty;
        const o = obj as Record<string, unknown>;
        return {
            issuingCountry: str(
                o.issuing_country ?? o.IssuingCountry ?? o.issuingCountry ?? o.ISSUING_COUNTRY,
            ),
            placeOfBirth: str(
                o.place_of_birth ?? o.PlaceOfBirth ?? o.placeOfBirth ?? o.PLACE_OF_BIRTH,
            ),
            isMeeting: normalizeProfileIsMeeting(
                o.is_meeting ?? o.IsMeeting ?? o.isMeeting ?? o.IS_MEETING,
            ),
            occupation: str(o.Occupation ?? o.occupation ?? o.designation ?? o.Designation),
            otherDirectorship: str(
                o.other_directorship ?? o.OtherDirectorship ?? o.otherDirectorship ?? o.OTHER_DIRECTORSHIPS,
            ),
            newRegNo: str(o.NEW_REG_NO ?? o.newRegNo ?? o.NewRegNo),
        };
    } catch {
        return empty;
    }
}

const LOCAL_ADDRESS_FIELD_SUFFIXES = [
    'STREET01',
    'STREET02',
    'STREET03',
    'POST_CODE',
    'CITY',
    'STATE',
    'COUNTRY',
] as const;

function mapLocalAddressToRegister(
    addr: Record<string, unknown> | null | undefined,
    prefix: string,
): Record<string, string> {
    const p = prefix ? `${prefix}_` : '';
    const out: Record<string, string> = {};
    for (const suffix of LOCAL_ADDRESS_FIELD_SUFFIXES) {
        out[`${p}${suffix}`] = '';
    }
    if (!addr || typeof addr !== 'object') return out;

    const street01 = str(addr.street01 ?? addr.street1 ?? addr.addressLine1 ?? addr.STREET01);
    const street02 = str(addr.street02 ?? addr.street2 ?? addr.addressLine2 ?? addr.STREET02);
    const street03 = str(addr.street03 ?? addr.street3 ?? addr.addressLine3 ?? addr.STREET03);
    const postCode = str(
        addr.post_code ?? addr.postcode ?? addr.postalCode ?? addr.POST_CODE ?? addr.postcodeCode,
    );
    const city = str(addr.city ?? addr.cityCode ?? addr.CITY);
    const state = str(addr.state ?? addr.stateCode ?? addr.STATE);
    const country = str(addr.country ?? addr.countryCode ?? addr.COUNTRY);

    out[`${p}STREET01`] = street01;
    out[`${p}STREET02`] = street02;
    out[`${p}STREET03`] = street03;
    out[`${p}POST_CODE`] = postCode;
    out[`${p}CITY`] = city;
    out[`${p}STATE`] = state;
    out[`${p}COUNTRY`] = country;

    return out;
}

/** Corporate/Individual Contact 주소 객체 → Register STREET01 등 (외부 mapper 공용) */
export function mapLocalAddressToRegisterFromCommon(
    addr: Record<string, unknown> | null | undefined,
    prefix: string,
): Record<string, string> {
    return mapLocalAddressToRegister(addr, prefix);
}

/** Contact → Register 매핑 패치에서 주소 필드만 비움 (ADD 시 프로필만 바인딩) */
export function stripAddressFromContactRegisterPatch(patch: Record<string, unknown>): void {
    Object.assign(patch, mapLocalAddressToRegister(null, ''));
    Object.assign(patch, mapLocalAddressToRegister(null, 'SERVICE'));
}

/** Contact 선택 해제·재선택 시 이전 값이 남지 않도록 빈 패치 */
export function emptyContactRegisterPatch(): Record<string, unknown> {
    return {
        CONTACT_CD: '',
        CONTACT_REG_NO: '',
        ID_TYPE_CD: '',
        NATIONALITY_CD: '',
        CONTACT_EMAIL: '',
        MOBILE_NO: '',
        AREA_CODE: '',
        COUNTRY_CD: '',
        NOTE: '',
        DATE_OF_BIRTH: '',
        DATE_OF_EXPIRY: '',
        ISSUING_COUNTRY: '',
        PLACE_OF_BIRTH: '',
        IS_MEETING: null,
        BUSINESS_OCCUPATION: '',
        DESIGNATION: '',
        Occupation: '',
        OTHER_DIRECTORSHIPS: '',
        NEW_REG_NO: '',
        ...mapLocalAddressToRegister(null, ''),
        ...mapLocalAddressToRegister(null, 'SERVICE'),
    };
}

function collectProfileJsonSources(contact: Record<string, unknown>): unknown[] {
    return [
        contact.CONTACT_PROFILE_JSON_DATA,
        contact.contactProfileJsonData,
        contact.JSON_DATA,
        contact.jsonData,
        contact.CONTACT_JSON_DATA,
        contact.contactJsonData,
    ].filter((v) => v != null && v !== '');
}

/** Contact API 응답 → Register 공통 필드 (인적사항 + 프로필 + 주소) */
export function buildContactRegisterCommonPatch(contact: Record<string, any>): Record<string, any> {
    const patch: Record<string, any> = {
        ...emptyContactRegisterPatch(),
        CONTACT_CD: str(contact.CONTACT_CD ?? contact.contactCd),
        CONTACT_REG_NO: str(contact.CONTACT_REG_NO ?? contact.contactRegNo),
        ID_TYPE_CD: str(contact.ID_TYPE_CD ?? contact.idTypeCd),
        NATIONALITY_CD: str(contact.NATIONALITY_CD ?? contact.nationalityCd),
        CONTACT_EMAIL: str(contact.CONTACT_EMAIL ?? contact.contactEmail),
        MOBILE_NO: str(contact.MOBILE_NO ?? contact.mobileNo),
        AREA_CODE: str(contact.AREA_CODE ?? contact.areaCode),
        COUNTRY_CD: str(contact.COUNTRY_CD ?? contact.countryCd ?? contact.COUNTRY_CODE),
        NOTE: str(contact.NOTE ?? contact.note),
        DATE_OF_BIRTH: dateOnly(contact.DATE_OF_BIRTH ?? contact.dateOfBirth),
        DATE_OF_EXPIRY: dateOnly(contact.PASSPORT_NO_EXPIRY_DATE ?? contact.passportNoExpiryDate),
    };

    let extended = {
        issuingCountry: '',
        placeOfBirth: '',
        isMeeting: null as number | null,
        otherDirectorship: '',
        newRegNo: '',
    };
    for (const raw of collectProfileJsonSources(contact)) {
        const parsed = parseExtendedProfileJsonFields(raw);
        if (parsed.issuingCountry && !extended.issuingCountry) {
            extended.issuingCountry = parsed.issuingCountry;
        }
        if (parsed.placeOfBirth && !extended.placeOfBirth) {
            extended.placeOfBirth = parsed.placeOfBirth;
        }
        if (parsed.isMeeting != null && extended.isMeeting == null) {
            extended.isMeeting = parsed.isMeeting;
        }
        if (parsed.otherDirectorship && !extended.otherDirectorship) {
            extended.otherDirectorship = parsed.otherDirectorship;
        }
        if (parsed.newRegNo && !extended.newRegNo) {
            extended.newRegNo = parsed.newRegNo;
        }
    }

    patch.ISSUING_COUNTRY = extended.issuingCountry;
    patch.PLACE_OF_BIRTH = extended.placeOfBirth;
    patch.IS_MEETING = extended.isMeeting;
    patch.OTHER_DIRECTORSHIPS = extended.otherDirectorship;

    const categoryCd = str(contact.CATEGORY_CD ?? contact.categoryCd).toUpperCase();
    // Member/Transfer 주소 저장 분기(법인→L_CONTACT_BRANCH)에 필수 — 누락 시 B09002 로 ADDRESS 만 쌓임
    if (categoryCd) {
        patch.CATEGORY_CD = categoryCd;
    }
    if (categoryCd === 'B09001') {
        patch.NEW_REG_NO = extended.newRegNo;
        // 법인 주소는 Entity+Position 기준 L_CONTACT_BRANCH(ADDR_JSON_DATA)만 사용.
        // Contact GET 의 branch(포지션 미필터)를 Register STREET01 로 넣지 않음.
        return patch;
    }

    try {
        const formState = hydrateMyContactDetailForms(contact as Record<string, unknown>);
        const residential = formState?.addressData?.localAddress as Record<string, unknown> | undefined;
        Object.assign(patch, mapLocalAddressToRegister(residential, ''));

        const service = formState?.contactInfoData?.altLocalAddress as Record<string, unknown> | undefined;
        Object.assign(patch, mapLocalAddressToRegister(service, 'SERVICE'));

        const countryCd = str(formState?.contactInfoData?.countryCode);
        if (countryCd) patch.COUNTRY_CD = countryCd;
    } catch {
        // 주소 hydrate 실패 시 인적사항만 유지
    }

    return patch;
}

/** Register DATE_OF_EXPIRY → Global Contact PASSPORT_NO_EXPIRY_DATE */
export function appendPassportExpiryToContactUpdate(
    jsonData: Record<string, unknown>,
    updateData: Record<string, unknown>,
): void {
    if (jsonData.DATE_OF_EXPIRY === undefined) return;
    const expiry = dateOnly(jsonData.DATE_OF_EXPIRY);
    updateData.passportNoExpiryDate = expiry || null;
}

/** Register jsonData (SEX / SEX_TEXT / Gender / ISMALE) → Global Contact profile gender */
export function resolveRegisterGenderForContact(jsonData: Record<string, unknown>): string | undefined {
    const sex = String(
        jsonData.SEX
        ?? jsonData.SEX_TEXT
        ?? jsonData.Gender
        ?? jsonData.GENDER
        ?? jsonData.MEMBER_SEX_TEXT
        ?? '',
    ).trim();
    if (sex) return sex;

    const isMale = jsonData.ISMALE ?? jsonData.isMale;
    if (
        isMale === true
        || isMale === 'true'
        || isMale === 1
        || isMale === '1'
        || isMale === 'Y'
        || isMale === 'y'
    ) {
        return 'Male';
    }
    if (
        isMale === false
        || isMale === 'false'
        || isMale === 0
        || isMale === '0'
        || isMale === 'N'
        || isMale === 'n'
    ) {
        return 'Female';
    }
    return undefined;
}

/** Register COUNTRY_CD → Global Contact PUT countryCode (L_GLOBAL_CONTACT.COUNTRY_CD) */
export function appendRegisterCountryCdToContactUpdate(
    jsonData: Record<string, unknown>,
    updateData: Record<string, unknown>,
): void {
    if (jsonData.COUNTRY_CD === undefined) return;
    updateData.countryCode = String(jsonData.COUNTRY_CD ?? '');
}

export function hasRegisterCessationDate(value: unknown): boolean {
    const raw = String(value ?? '').trim();
    if (!raw || raw === '-') return false;
    return raw.split(/[ T]/)[0] !== '';
}

/** Register jsonData → 퇴임일 (CESSATION_DATE / DOCEASE 등) */
export function resolveRegisterCessationDate(jsonData: Record<string, unknown>): string | null {
    const raw = String(
        jsonData.CESSATION_DATE ?? jsonData.cessationDate ?? jsonData.DOCEASE ?? '',
    ).trim();
    return raw || null;
}

const REGISTER_PROFILE_HISTORY_STRIP_KEYS = new Set([
    '_PROFILE_FROM_HISTORY',
    '_ENTITY_OFFICER',
    '_ENTITY_BENEFICIAL_OWNER',
    '_ENTITY_MEMBER',
    '_RB_SOURCE',
    '_RB_SOURCE_SHORT',
    '_RB_PARSE_ERROR',
    '_RB_PARSE_ERROR_MESSAGE',
    '_RB_RAW_JSON',
    'classification',
    'ownership',
    'sm',
    'FORMER_NAMES',
    'FORMER_NAME',
    'ALIASES',
    'ALIAS',
]);

/** 퇴임 Officer profile-history POST body — UI 메타 제외 Register jsonData */
export function buildRegisterProfileForHistorySync(
    jsonData: Record<string, unknown>,
): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(jsonData || {})) {
        if (REGISTER_PROFILE_HISTORY_STRIP_KEYS.has(key)) continue;
        out[key] = value;
    }
    return out;
}

/** MY Register — Global Contact 주소 블록 표시 여부 (Entity 상세 + Add 시 Contact 선택) */
export function shouldShowRegisterGlobalContactAddress(opts: {
    isJurisdictionMY: boolean;
    contactCd: string;
    entityCd: string;
    useGlobalContactAddressProp: boolean;
    addModeContactPicker: boolean;
    /** profile history 스냅샷 — live Contact 주소 대신 스냅샷 표시 */
    profileFromHistory?: boolean;
}): boolean {
    if (!opts.isJurisdictionMY) return false;
    if (!str(opts.contactCd) || !str(opts.entityCd)) return false;
    if (opts.profileFromHistory) return false;
    return opts.useGlobalContactAddressProp || opts.addModeContactPicker;
}
